/**
 * YagiChatBot 用の型と Vue 非依存ヘルパ。
 */

export type YagiChatFormValue = Record<string, unknown>

export type YagiChatQuickReply = {
  label: string
  value: string
}

export type YagiChatAutoFillFields = {
  company_name?: string
  industry?: string
  job?: string
  values?: string
}

export type YagiChatQuestion = {
  id: string
  question: string
  field: string
  description?: string
  placeholder?: string
  type?: string
  allowNone?: boolean
  quickReplies?: YagiChatQuickReply[]
  skipIf?: (value: YagiChatFormValue) => boolean
  autoSkipIfFilled?: boolean
  autoFillFields?: YagiChatAutoFillFields
}

export type CompanyExtractData = {
  company_name?: string
  industry?: string
  job?: string
  values?: string
}

export type CompanyExtractResponse = {
  success?: boolean
  data?: CompanyExtractData
  error?: string
}

export type YagiAiResultCallbacks = {
  onAccept?: () => void
  onRegenerate?: () => void
  onEdit?: () => void
}

export type YagiChatMessage =
  | {
      type: 'question'
      text: string
      description?: string
      placeholder?: string
      questionId?: string
    }
  | { type: 'answer'; text: string }
  | {
      type: 'system'
      text: string
      skippedQuestions?: string[]
      skippedIndices?: number[]
    }
  | { type: 'ai-result'; text: string; active?: boolean }
  | {
      type: 'company-info'
      text: string
      companyData: CompanyExtractData
      editableFields?: string[]
    }

export type CloseDialogOptions = {
  suppressCloseEvent?: boolean
}

export function formatYagiChatDate(now = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}.${month}.${day}`
}

export function trackYagiChatGtag(
  event: string,
  params: Record<string, unknown>
): void {
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag === 'function') {
    gtag('event', event, params)
  }
}

export function findQuestionIndexById(
  questions: YagiChatQuestion[],
  questionId: string
): number {
  return questions.findIndex((q) => q.id === questionId)
}

export function collectCompanyAutofill(
  companyData: CompanyExtractData,
  autoFillFields: YagiChatAutoFillFields,
  currentValue: YagiChatFormValue
): { updatedValue: YagiChatFormValue; filledFields: string[] } {
  const updatedValue = { ...currentValue }
  const filledFields: string[] = []

  if (companyData.company_name && autoFillFields.company_name) {
    updatedValue[autoFillFields.company_name] = companyData.company_name
    filledFields.push(autoFillFields.company_name)
  }
  if (companyData.industry && autoFillFields.industry) {
    updatedValue[autoFillFields.industry] = companyData.industry
    filledFields.push(autoFillFields.industry)
  }
  if (companyData.job && autoFillFields.job) {
    updatedValue[autoFillFields.job] = companyData.job
    filledFields.push(autoFillFields.job)
  }
  if (companyData.values && autoFillFields.values) {
    updatedValue[autoFillFields.values] = companyData.values
    filledFields.push(autoFillFields.values)
  }

  return { updatedValue, filledFields }
}

export function isFilledString(value: unknown): boolean {
  return !!(
    value &&
    typeof (value as { trim?: () => string }).trim === 'function' &&
    (value as { trim: () => string }).trim()
  )
}

export function fieldValueAsString(
  value: YagiChatFormValue,
  field: string
): string {
  const raw = value[field]
  return typeof raw === 'string' ? raw : raw != null ? String(raw) : ''
}
